import "@khan/guide-ui/message-bubble.css";

import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import moment from "moment";
import * as React from "react";
import stringHash from "string-hash";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {Popover, PopoverContent} from "@khanacademy/wonder-blocks-popover";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    BodyText,
    Heading,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {
    bigqueryTraceURL,
    langfuseSessionURL,
    langfuseTraceURL,
} from "@khan/guide-core";
import type {DiagramData} from "@khan/guide-types";
import {
    Diagram,
    FlaggedAnswerText,
    FormattedText,
    GuideColors,
} from "@khan/guide-ui";
import {doNotTranslate} from "@khan/i18n";
import {useUserAttributes} from "@khan/shared-core";
import {a11yStyles} from "@khan/shared-styles";

import {CoeditorSnapshot} from "./coeditor-snapshot.tsx";
import type {
    guideTranscript_aiGuideThread_coeditingDocument_snapshots as CoeditingSnapshot,
    guideTranscript_aiGuideThread_coeditingDocument_snapshots_text as CoeditorState,
    guideTranscript_aiGuideThread_insights as Insights,
    guideTranscript_aiGuideThread_interactions as Interaction,
    guideTranscript_aiGuideThread as Thread,
} from "./guide-transcripts/__graphql-types__/guideTranscript.ts";
import MaybeEssayReportLink from "./maybe-essay-report-link.tsx";
import {combineInteractionsAndSnapshots} from "./utils.ts";

import imageIcon from "@phosphor-icons/core/regular/image.svg";
import infoIcon from "@phosphor-icons/core/regular/info.svg";
import warningIcon from "@phosphor-icons/core/regular/warning.svg";

interface Props {
    thread: Thread;
    systemChatStyle?: CSSProperties;
    userChatStyle?: CSSProperties;
}

interface MessageProps {
    type: "incoming" | "outgoing";
    content: string;
    flagged: boolean;
    userInputImage?: boolean;
    bubbleStyles?: CSSProperties;
    diagrams?: Array<DiagramData>;
}

interface MessagesProps {
    interactions: Array<Interaction>;
    coeditingSnapshots: Array<CoeditingSnapshot> | null;
    questionBubbleStyles?: CSSProperties;
    answerBubbleStyle?: CSSProperties;
}

interface InsightsProps {
    insights: Insights;
}

interface AnswerProps {
    answer: string;
    coeditorState?: CoeditorState;
    flagged: boolean;
    hasAccessToAIGuideDev: boolean;
    interactionId: string;
    interactionTraceId: string | null | undefined;
    stopped: boolean;
    userInputImage?: boolean;
    bubbleStyles?: CSSProperties;
    diagrams?: Array<DiagramData>;
}

interface QuestionProps {
    question: string;
    coeditorState?: CoeditorState;
    flagged: boolean;
    timestamp: string;
    bubbleStyles?: CSSProperties;
    isFirstInteraction?: boolean;
}

interface ImageProps {
    imageSummary: Nullable<string>;
    coeditorState?: CoeditorState;
    flagged: boolean;
}

interface ImageIconPopoverProps {
    content: string;
}

const StyledDl = addStyle("dl");
const StyledSpan = addStyle("span");

const ImageIconPopover = ({content}: ImageIconPopoverProps) => (
    <Popover
        placement="left"
        dismissEnabled
        content={
            <PopoverContent
                closeButtonVisible
                content={content}
                title={t`Image Placeholder`}
            />
        }
    >
        {({open}) => (
            <IconButton
                testId="image-placeholder"
                aria-label={t`Image Placeholder`}
                kind="tertiary"
                icon={infoIcon}
                onClick={open}
                style={{alignSelf: "center"}}
            />
        )}
    </Popover>
);

export const ModerationWarningIconWithPopover = ({
    popoverContent = t`This interaction with Khanmigo has been flagged by Khan Academy's moderation system.`,
}) => (
    <Popover
        placement="left"
        dismissEnabled
        content={
            <PopoverContent
                closeButtonVisible
                content={popoverContent}
                title={t`Moderation Warning`}
                actions={
                    <Link href="/r/khanmigo-moderation">{t`More Info`}</Link>
                }
            />
        }
    >
        {({open}) => (
            <IconButton
                aria-label={t`Moderation Warning`}
                icon={warningIcon}
                actionType="destructive"
                kind="tertiary"
                onClick={open}
                style={{alignSelf: "center"}}
            />
        )}
    </Popover>
);

const Message = ({
    type,
    content,
    flagged,
    userInputImage,
    bubbleStyles,
    diagrams,
}: MessageProps): React.ReactElement => {
    const isOutgoing = type === "outgoing";

    // If a user message was flagged during the conversation, the text content
    // will just be "Flagged". In this case, we want to show a special message.
    // If a user message was flagged after the conversation, by the district
    // alert classifier, we still want to show the original message content.
    const trimmedContent =
        flagged && content === "Flagged" && !isOutgoing ? (
            <FlaggedAnswerText hasImage={userInputImage} />
        ) : (
            <FormattedText text={content.trim()} />
        );
    return (
        <div className={isOutgoing ? "ai-guide-sent-message" : ""}>
            <Body
                style={[
                    styles.messageBubble,
                    isOutgoing ? styles.userMessage : styles.botMessage,
                    bubbleStyles,
                ]}
                testId={isOutgoing ? "sent-message" : "ai-message"}
            >
                <>
                    {trimmedContent}
                    {diagrams?.map((diagram) => (
                        <Diagram
                            key={`${stringHash(diagram.code)}`}
                            diagramData={diagram}
                        />
                    ))}
                </>
            </Body>
        </div>
    );
};

const Question = ({
    question,
    coeditorState,
    flagged,
    bubbleStyles,
    timestamp,
    imageSummary,
    userInputImage,
    isFirstInteraction,
}: QuestionProps &
    ImageProps & {
        userInputImage: Nullable<boolean>;
    }): React.ReactElement | null => {
    const outgoingMessage = (
        <View tag="dt">
            <View style={{margin: "auto"}}>
                <BodyText size="xsmall" style={styles.messageTimestamp}>
                    {moment(timestamp).format("l LT")}
                </BodyText>
            </View>
            {userInputImage && (
                <Image
                    imageSummary={imageSummary}
                    coeditorState={coeditorState}
                    flagged={flagged}
                />
            )}
            {question && (
                <View style={styles.row}>
                    {flagged && <ModerationWarningIconWithPopover />}
                    <View style={styles.messageBubbleWrapper}>
                        <Message
                            type="outgoing"
                            content={question}
                            flagged={flagged}
                            bubbleStyles={bubbleStyles}
                        />
                    </View>
                </View>
            )}
            {!question && isFirstInteraction && (
                // User has started a tutoring session; there is no question,
                // but this elicits an initial static message from Khanmigo.
                // Adding this SR-only message to ensure we have an initial `dt`
                <StyledSpan style={a11yStyles.srOnly}>
                    {t`User has started a tutoring session.`}
                </StyledSpan>
            )}
        </View>
    );

    if (coeditorState) {
        return (
            <CoeditorSnapshot coeditorState={coeditorState} alignment="end">
                {outgoingMessage}
            </CoeditorSnapshot>
        );
    }

    return outgoingMessage;
};

const Answer = ({
    answer,
    coeditorState,
    flagged,
    hasAccessToAIGuideDev,
    interactionId,
    interactionTraceId,
    stopped,
    userInputImage,
    bubbleStyles,
    diagrams,
}: AnswerProps): React.ReactElement | null => {
    if (!answer && !flagged) {
        return null;
    }
    const incomingMessage = (
        <View tag="dd" style={styles.messageBubbleWrapper}>
            <Message
                type="incoming"
                content={answer}
                flagged={flagged}
                userInputImage={userInputImage}
                bubbleStyles={bubbleStyles}
                diagrams={diagrams}
            />

            {(hasAccessToAIGuideDev || stopped) && (
                <View style={styles.messageFootnote}>
                    {hasAccessToAIGuideDev && (
                        <View style={styles.devLinks}>
                            <Link
                                href={`/api/internal/_ai-guide/interaction-completion/${interactionId}`}
                                target="_blank"
                            >
                                {doNotTranslate`View completion`}
                            </Link>
                            <Link
                                href={`/api/internal/_ai-guide/interaction-completion-replay/${interactionId}`}
                                target="_blank"
                            >
                                {doNotTranslate`Edit and replay`}
                            </Link>
                            <Link
                                href={`/api/internal/_ai-guide/interaction-completion-near/${interactionId}`}
                                target="_blank"
                            >
                                {doNotTranslate`View (maybe) related`}
                            </Link>
                            {interactionTraceId && (
                                <Link
                                    target="_blank"
                                    href={langfuseTraceURL(interactionTraceId)}
                                >
                                    {t`View Trace in Langfuse`}
                                </Link>
                            )}
                            {interactionId && (
                                <Link
                                    target="_blank"
                                    href={bigqueryTraceURL(interactionId)}
                                >
                                    {t`View Trace in BigQuery`}
                                </Link>
                            )}
                        </View>
                    )}

                    {stopped && (
                        <LabelSmall style={styles.responseWasStoppedByUser}>
                            {t`User stopped response`}
                        </LabelSmall>
                    )}
                </View>
            )}
        </View>
    );

    if (coeditorState) {
        return (
            <CoeditorSnapshot coeditorState={coeditorState} alignment="start">
                {incomingMessage}
            </CoeditorSnapshot>
        );
    }

    return incomingMessage;
};

const Image = ({
    imageSummary,
    coeditorState,
    flagged,
}: ImageProps): React.ReactElement | null => {
    const imageTooltipContent = imageSummary
        ? t`We do not store any images that are sent to Khanmigo. An AI generated description of the image is shown here instead for your reference.`
        : t`We do not store any images that are sent to Khanmigo. This is a placeholder of where in the conversation the image was sent.`;

    const moderationPopoverContent = t`This image has been flagged by Khan Academy's moderation system. We do not store any images that are sent to Khanmigo.`;

    const outgoingMessage = (
        <View style={styles.image}>
            <View style={styles.imageIconAndInfoIcon}>
                {flagged ? (
                    <ModerationWarningIconWithPopover
                        popoverContent={moderationPopoverContent}
                    />
                ) : (
                    <ImageIconPopover content={imageTooltipContent} />
                )}
                <View style={styles.imageIcon}>
                    <PhosphorIcon
                        icon={imageIcon}
                        size="large"
                        color={semanticColor.core.foreground.neutral.subtle}
                    />
                </View>
            </View>
            <View style={styles.imageSummary}>
                <LabelSmall>{imageSummary}</LabelSmall>
            </View>
        </View>
    );

    if (coeditorState) {
        return (
            <CoeditorSnapshot coeditorState={coeditorState} alignment="end">
                {outgoingMessage}
            </CoeditorSnapshot>
        );
    }

    return outgoingMessage;
};

const Messages = (props: MessagesProps): React.ReactElement => {
    const attributes = useUserAttributes();
    const hasAccessToAIGuideDev = attributes?.hasAccessToAIGuideDev ?? false;
    const interactionsWithCoeditorState = combineInteractionsAndSnapshots(
        props.interactions,
        props.coeditingSnapshots,
    );

    if (interactionsWithCoeditorState.length === 0) {
        return (
            <View style={{margin: "auto"}}>
                <Body style={styles.emptyInteractionBody}>
                    {t`This conversation is empty.`}
                </Body>
            </View>
        );
    }

    return (
        <StyledDl style={styles.messages}>
            {interactionsWithCoeditorState.map((interaction, index) => (
                <React.Fragment key={interaction.id ?? index}>
                    {(interaction.question || interaction.answer) && (
                        <>
                            {/**
                             * Everything that prompts the answer from Khanmigo
                             * needs to be inside Question (or more importantly,
                             * the `dt` element within Question).
                             * That includes timestamp, images, etc.
                             * The direct children of a `dl` element should be
                             * only `dt` and `dd` elements.
                             * https://khanacademy.slack.com/archives/C0A4SN2NQ/p1756322946158769
                             */}
                            <Question
                                question={interaction.question}
                                coeditorState={
                                    interaction.questionCoeditorState
                                }
                                flagged={interaction.flagged}
                                bubbleStyles={props.questionBubbleStyles}
                                timestamp={interaction.createdAt}
                                imageSummary={
                                    interaction.imageMetadata?.llmSummary
                                }
                                userInputImage={interaction.userInputImage}
                            />
                            <Answer
                                answer={interaction.answer}
                                coeditorState={interaction.answerCoeditorState}
                                flagged={interaction.flagged}
                                userInputImage={Boolean(
                                    interaction.userInputImage,
                                )}
                                hasAccessToAIGuideDev={hasAccessToAIGuideDev}
                                interactionId={interaction.id}
                                interactionTraceId={interaction.traceID}
                                stopped={interaction.responseWasStoppedByUser}
                                bubbleStyles={props.answerBubbleStyle}
                                diagrams={interaction.diagrams}
                            />
                        </>
                    )}
                </React.Fragment>
            ))}
        </StyledDl>
    );
};

const ThreadInsights = ({insights}: InsightsProps): React.ReactElement => (
    <View>
        <Heading size="large">{doNotTranslate`Insights`}</Heading>
        <LabelSmall>
            {doNotTranslate`The following are insights collected from this conversation.`}
        </LabelSmall>
        <Strut size={spacing.small_12} />
        <ul style={{listStyle: "inside"}}>
            <li>{`Title: ${insights.title || ""}`}</li>
            <li>{`Summary: ${insights.summary || ""}`}</li>
            <li>{`Interests: ${insights.interests || ""}`}</li>
            <li>{`Topics: ${insights.topics || ""}`}</li>
            <li>{`Off Topic: ${insights.offTopic || ""}`}</li>
            <li>{`Safety: ${insights.safety || ""}`}</li>
            <li>{`Languages: ${insights.languages || ""}`}</li>
            <li>{`Math: ${insights.math || ""}`}</li>
        </ul>
        <Strut size={spacing.small_12} />
    </View>
);

export default function ConversationView({
    thread,
    systemChatStyle,
    userChatStyle,
}: Props): React.ReactElement {
    const attributes = useUserAttributes();
    const hasAccessToAIGuideDev = attributes?.hasAccessToAIGuideDev ?? false;
    return (
        <View>
            <MaybeEssayReportLink thread={thread} />
            {hasAccessToAIGuideDev &&
                anyThreadInteractionsHaveTraceIds(thread) && (
                    <Link target="_blank" href={langfuseSessionURL(thread.id)}>
                        {t`View thread traces in Langfuse`}
                    </Link>
                )}
            {thread.summary && (
                <View>
                    <Strut size={spacing.xSmall_8} />
                    <LabelLarge>{t`Summary`}:</LabelLarge>
                    <Strut size={spacing.xSmall_8} />
                    <Body>{thread.summary}</Body>
                </View>
            )}
            <Strut size={spacing.xLarge_32} />
            <View style={styles.separator} />
            <Strut size={spacing.xLarge_32} />
            <Messages
                interactions={thread.interactions}
                coeditingSnapshots={thread.coeditingDocument?.snapshots ?? null}
                questionBubbleStyles={userChatStyle}
                answerBubbleStyle={systemChatStyle}
            />
            {hasAccessToAIGuideDev && thread.insights && (
                <>
                    <Strut size={spacing.small_12} />
                    <ThreadInsights insights={thread.insights} />
                </>
            )}
        </View>
    );
}

const anyThreadInteractionsHaveTraceIds = (thread: Thread) => {
    // if any interaction has a traceID, return true.
    for (const interaction of thread.interactions) {
        if (interaction.traceID) {
            return true;
        }
    }
    return false;
};

const messageBubblePadding = 15;
const botMessageMargin = spacing.small_12;

const styles = StyleSheet.create({
    image: {
        justifyContent: "flex-end",
        gap: spacing.xSmall_8,
        marginInlineEnd: spacing.small_12,
        marginBlockEnd: botMessageMargin,
    },
    imageIconAndInfoIcon: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
    },
    imageIcon: {
        padding: spacing.large_24,
        borderRadius: spacing.xSmall_8,
        backgroundColor: semanticColor.core.background.neutral.subtle,
        borderColor: semanticColor.core.border.neutral.default,
        borderWidth: 1,
    },
    imageSummary: {
        textAlign: "end",
        maxWidth: "50%",
        marginInlineStart: "auto",
    },

    // This is all imported form chatbot.jsx
    messages: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        listStyle: "none",
        background: semanticColor.core.background.base.default,
        borderRadius: 8,
        color: semanticColor.core.foreground.neutral.strong,
        marginBlockEnd: spacing.large_24,
    },
    messageBubbleWrapper: {
        display: "flex",
        flexDirection: "column",
        marginBlockStart: spacing.xSmall_8,
        marginBlockEnd: spacing.xSmall_8,
        maxWidth: "90%",
        width: "fit-content",
    },
    messageBubble: {
        flexDirection: "row",
        borderRadius: 8,
        padding: messageBubblePadding,
    },
    userMessage: {
        backgroundColor: GuideColors.darkPurple,
        color: semanticColor.core.foreground.knockout.default,
        marginInlineEnd: spacing.small_12,
        alignSelf: "flex-end",
    },
    botMessage: {
        alignSelf: "flex-start",
        backgroundColor: GuideColors.peach,
        marginInlineStart: spacing.small_12,
    },
    messageFootnote: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBlockStart: spacing.xSmall_8,
        marginInlineStart: botMessageMargin + messageBubblePadding,
    },
    responseWasStoppedByUser: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    row: {
        alignItems: "flex-end",
        justifyContent: "flex-end",
        flexDirection: "row",
        width: "100%",
    },
    messageTimestamp: {
        color: semanticColor.core.foreground.neutral.default,
        padding: spacing.xSmall_8,
    },
    emptyInteractionBody: {
        padding: 50,
    },
    separator: {
        height: 1,
        backgroundColor: semanticColor.core.border.neutral.subtle,
    },
    devLinks: {
        flexDirection: "row",
        gap: spacing.small_12,
    },
});
